import { Media } from '@/utils/EpisodeUtil'
import { episodeToMedia, isASneakPeek } from '@/utils/EpisodeUtil/EpisodeUtil'
import { TypedTrackFn } from '@/utils/analytics'
import {
  getObjectFromLocalStorage,
  GUILD_CONTINUE_WATCHING_COLLECTION,
  writeToLocalStorage,
} from '@/utils/local-storage'
import { logger } from '@/utils/logging'
import { getWebClient } from '../ApolloClient'
import { getCollection, SNEAK_PEEK_TO_PRIMARY_ENTRY_ID } from '../CmsService/Collection'
import { getEpisodeByGuid } from '../ProjectsService/ProjectsService'
import { Episode } from '../ProjectsService/types'

export async function saveGuildContinueWatching(
  episode: Episode,
  track: TypedTrackFn,
  locale: string,
): Promise<boolean> {
  const sneakPeekToPrimaryMap = await getCollection({ locale }, SNEAK_PEEK_TO_PRIMARY_ENTRY_ID)
  let media = episodeToMedia(episode)
  try {
    const isSneakPeek = isASneakPeek(episode)
    if (isSneakPeek) {
      if (sneakPeekCompilations.includes(episode.guid)) {
        logger().info(
          'A compliation sneak peek was watched. Nothing will be saved to the guild continue watching collection.',
          { episode, media },
        )
        // We currently don't have a good way to handle these, so return true because we can't save and shouldn't try again.
        return true
      }

      if (!sneakPeekToPrimaryMap) {
        logger().error(
          'Failed to save a sneak peek for guild continue watching because the required contentful collection is missing',
          {
            episode,
            media,
          },
        )
        // log error if the contentful collection fetch fails.
        return true
      }

      const primary = sneakPeekToPrimaryMap?.customData[episode.guid]
      if (!primary) {
        logger().error(
          'Failed to save a sneak peek for guild continue watching because the required config is missing',
          {
            episode,
            media,
          },
        )
        // even though we didn't save, return true because we can't save and shouldn't try again; this is not an error we can recover from.
        return true
      }

      const response = await getEpisodeByGuid({ guid: primary.id }, getWebClient())

      const primaryEpisode = response

      if (!primaryEpisode) {
        logger().error('Failed to retrieve the primary episode for a sneak peek for guild continue watching', {
          episode,
          media,
          primaryEpisode,
          response,
        })
        return false
      }

      media = episodeToMedia(primaryEpisode, { progress: primary.progress, position: primary.position })
    }

    // The episode object that is passed in or fetched here was retrieved while the user is not a guild member yet.
    // Because the user is not a guild member, the unavailable reason will be set.
    // We are loading this now and saving the entire object instead of fetching it later so that performance on load
    // after the user returns from joining the guild is instant. So we need to remove the unavailable reason here
    // anticipating that user will join the guild and will only see the thumbnail generated by this object after joining the guild.
    // The actual watching of content is unaffected and only logged in guild members can actually watch the content.
    media.unavailableReason = undefined

    const existingCollection = getObjectFromLocalStorage<Media[]>(GUILD_CONTINUE_WATCHING_COLLECTION) ?? []

    // If this episode is already in the array, remove it, then place the new version at the front.
    // This makes it so that whatever has been watched most recently will be at the front of the array
    // while preventing duplicate entries in the event that the same episode is watched more than once.
    const updated = existingCollection.filter((m) => m.guid !== media.guid)
    updated.unshift(media)

    writeToLocalStorage(GUILD_CONTINUE_WATCHING_COLLECTION, updated)

    track('Guild Continue Watching Media Saved', { episode, media })
    logger().info(`Successfully saved a media to the guild continue watching collection.`, { episode, media })
    return true
  } catch (err) {
    logger().error(
      'An error occurred when attempting to save a media to the guild continue watching collection!',
      {
        episode,
        media,
      },
      err as Error,
    )
    return false
  }
}

export function useGuildContinueWatching(): { medias: Media[] } {
  return { medias: getObjectFromLocalStorage<Media[]>(GUILD_CONTINUE_WATCHING_COLLECTION) ?? [] }
}

// Right now there's not a good way to map a sneak peek episode to a project. Dry Bar has a sneak peek
// that is a compliation of clips from a bunch of guild-gated dry bar specials. So there's not a
// single special that would make sense to link to from the sneak peek. It would probably make the
// most sense to link to /watch/dry-bar-best-of. The continue watching rail is currently set up
// to link to a watchable, so we'll need to think about how to handle this type of scenario.
const sneakPeekCompilations = ['8ad8fe7f-afd5-40fb-b554-14f68eaa63d7']
